"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.determineCorrectV = determineCorrectV;
exports.findEthereumSig = findEthereumSig;
exports.getEthereumAddress = getEthereumAddress;
exports.getPublicKey = getPublicKey;
exports.requestKmsSignature = requestKmsSignature;
exports.sign = sign;
var _clientKms = require("@aws-sdk/client-kms");
var asn1 = _interopRequireWildcard(require("asn1.js"));
var _bn = _interopRequireDefault(require("bn.js"));
var _keccak = require("@ethersproject/keccak256");
var _transactions = require("@ethersproject/transactions");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
/* this asn1.js library has some funky things going on */
/* eslint-disable func-names */

const EcdsaSigAsnParse = asn1.define("EcdsaSig", function () {
  // parsing this according to https://tools.ietf.org/html/rfc3279#section-2.2.3
  this.seq().obj(this.key("r").int(), this.key("s").int());
});
const EcdsaPubKey = asn1.define("EcdsaPubKey", function () {
  // parsing this according to https://tools.ietf.org/html/rfc5480#section-2
  this.seq().obj(this.key("algo").seq().obj(this.key("a").objid(), this.key("b").objid()), this.key("pubKey").bitstr());
});
/* eslint-enable func-names */
function sign(_x, _x2) {
  return _sign.apply(this, arguments);
}
function _sign() {
  _sign = _asyncToGenerator(function* (digest, kmsCredentials) {
    const kms = new _clientKms.KMSClient(kmsCredentials);
    return kms.send(new _clientKms.SignCommand({
      // key id or 'Alias/<alias>'
      KeyId: kmsCredentials.keyId,
      Message: digest,
      // 'ECDSA_SHA_256' is the one compatible with ECC_SECG_P256K1.
      SigningAlgorithm: "ECDSA_SHA_256",
      MessageType: "DIGEST"
    }));
  });
  return _sign.apply(this, arguments);
}
function getPublicKey(_x3) {
  return _getPublicKey.apply(this, arguments);
}
function _getPublicKey() {
  _getPublicKey = _asyncToGenerator(function* (kmsCredentials) {
    const kms = new _clientKms.KMSClient(kmsCredentials);
    return kms.send(new _clientKms.GetPublicKeyCommand({
      KeyId: kmsCredentials.keyId
    }));
  });
  return _getPublicKey.apply(this, arguments);
}
function getEthereumAddress(publicKey) {
  // The public key is ASN1 encoded in a format according to
  // https://tools.ietf.org/html/rfc5480#section-2
  // I used https://lapo.it/asn1js to figure out how to parse this
  // and defined the schema in the EcdsaPubKey object
  const res = EcdsaPubKey.decode(publicKey, "der");
  let pubKeyBuffer = res.pubKey.data;

  // The public key starts with a 0x04 prefix that needs to be removed
  // more info: https://www.oreilly.com/library/view/mastering-ethereum/9781491971932/ch04.html
  pubKeyBuffer = pubKeyBuffer.slice(1, pubKeyBuffer.length);
  const address = (0, _keccak.keccak256)(pubKeyBuffer); // keccak256 hash of publicKey
  const EthAddr = `0x${address.slice(-40)}`; // take last 20 bytes as ethereum adress

  return EthAddr;
}
function findEthereumSig(signature) {
  const decoded = EcdsaSigAsnParse.decode(signature, "der");
  const r = decoded.r,
    s = decoded.s;
  const secp256k1N = new _bn.default("fffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141", 16); // max value on the curve
  const secp256k1halfN = secp256k1N.div(new _bn.default(2)); // half of the curve
  // Because of EIP-2 not all elliptic curve signatures are accepted
  // the value of s needs to be SMALLER than half of the curve
  // i.e. we need to flip s if it's greater than half of the curve
  // if s is less than half of the curve, we're on the "good" side of the curve, we can just return
  return {
    r,
    s: s.gt(secp256k1halfN) ? secp256k1N.sub(s) : s
  };
}
function requestKmsSignature(_x4, _x5) {
  return _requestKmsSignature.apply(this, arguments);
}
function _requestKmsSignature() {
  _requestKmsSignature = _asyncToGenerator(function* (plaintext, kmsCredentials) {
    try {
      const signature = yield sign(plaintext, kmsCredentials);
      if (signature.$metadata.httpStatusCode !== 200 || signature.Signature === undefined) throw new Error(`AWS KMS call failed with: ${JSON.stringify(signature)}`);
      return findEthereumSig(Buffer.from(signature.Signature));
    } catch (e) {
      throw new Error(`AWS KMS call failed with: ${JSON.stringify(e)}`);
    }
  });
  return _requestKmsSignature.apply(this, arguments);
}
function recoverPubKeyFromSig(msg, r, s, v) {
  return (0, _transactions.recoverAddress)(`0x${msg.toString("hex")}`, {
    r: `0x${r.toString("hex")}`,
    s: `0x${s.toString("hex")}`,
    v
  });
}
function determineCorrectV(msg, r, s, expectedEthAddr) {
  // This is the wrapper function to find the right v value
  // There are two matching signatues on the elliptic curve
  // we need to find the one that matches to our public key
  // it can be v = 27 or v = 28
  let v = 27;
  let pubKey = recoverPubKeyFromSig(msg, r, s, v);
  if (pubKey.toLowerCase() !== expectedEthAddr.toLowerCase()) {
    // if the pub key for v = 27 does not match
    // it has to be v = 28
    v = 28;
    pubKey = recoverPubKeyFromSig(msg, r, s, v);
  }
  return {
    pubKey,
    v
  };
}